import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ShoppingBag,
  Heart,
  ChevronRight,
  Icon,
  Truck,
  Plane,
} from "lucide-react";

const LINKS = [
  {
    id: "ecom",
    title: "TCS Ecom Account Opening",
    url: "https://www.tcsexpress.com/ecom",
    Icon: ShoppingBag,
  },
  {
    id: "sentiments",
    title: "TCS Sentiments",
    url: "https://sentimentsexpress.com/",
    Icon: Heart,
  },
  {
    id: "domestic",
    title: "TCS Domestic",
    url: "https://www.tcsexpress.com/domestic",
    Icon: Truck,
  },
  {
    id: "international",
    title: "TCS International",
    url: "https://www.tcsexpress.com/international",
    Icon: Plane,
  },
];

const popupOverlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.25 } },
};

const popupCard = {
  hidden: { opacity: 0, scale: 0.85, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 22, stiffness: 260, delay: 0.1 },
  },
  exit: { opacity: 0, scale: 0.9, y: 30, transition: { duration: 0.2 } },
};

const linkCard = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.35 + i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

const QRLink = () => {
  const [showPopup, setShowPopup] = useState(true);
  useEffect(() => {
    document.body.style.overflow = showPopup ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPopup]);
  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-start
                 px-5 py-8 font-[Helvetica,sans-serif]"
    >
      {/* Main content */}
      <div className="relative z-10 w-full max-w-[420px] flex flex-col items-center md:max-w-[500px]">
        {/* Avatar */}
        <div className="relative mb-6 animate-[qrlink-fade-in-down_0.8s_ease-out_both]">
          <div
            className="w-[90px] h-[90px] rounded-full p-1
                       bg-[length:300%_300%] animate-[qrlink-ring-spin_4s_linear_infinite]
                       md:w-[120px] md:h-[120px]"
            style={{
              background:
                "linear-gradient(135deg,#d40511,#f21e26,#ff6b6b,#d40511)",
              backgroundSize: "300% 300%",
              boxShadow:
                "0 0 30px rgba(212,5,17,0.2), 0 0 60px rgba(212,5,17,0.08)",
            }}
          >
            <img
              src="/logo512.png"
              alt="TCS Express Logo"
              className="w-full h-full rounded-full object-contain bg-white border-[3px] border-white p-3"
            />
          </div>
        </div>

        {/* Profile info */}
        <div className="text-center mb-6 animate-[qrlink-fade-in-up_0.8s_0.2s_ease-out_both]">
          <h1 className="text-[1.6rem] font-bold text-[#1a1a2e] tracking-tight mb-1 md:text-[1.35rem]">
            TCS Express
          </h1>
          <p className="text-sm text-[#1a1a2e]/50 font-light tracking-wide m-0">
            Your Trusted Delivery Partner
          </p>
        </div>

        {/* Divider */}
        {/* <div className="w-[60px] h-[3px] rounded-full bg-[#ee0109] opacity-80 my-2" /> */}

        {/* Links */}
        <div className="w-full flex flex-col gap-2">
          {LINKS.map((link, i) => (
            <motion.a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              custom={i}
              initial="hidden"
              animate="visible"
              variants={linkCard}
              className="group relative flex items-center w-full px-5 py-[1.1rem]
                         rounded-xl no-underline bg-[#2b2b2b]
                         transition-all duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]
                         hover:bg-[#333] hover:-translate-y-px
                         active:translate-y-0 active:scale-[0.98]
                         overflow-hidden
                         sm:px-4 sm:py-[0.85rem]"
            >
              {/* Gradient border glow on hover */}
              <span
                className="absolute inset-[-1px] rounded-[17px] opacity-0
                           group-hover:opacity-100 transition-opacity duration-350
                           pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg,transparent,#d40511,#f21e26,transparent)",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  padding: "1px",
                }}
              />

              {/* Icon */}
              <div
                className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center
                           mr-3 bg-transparent border-none
                           transition-all duration-350
                           group-hover:bg-gradient-to-br group-hover:from-[#d40511] group-hover:to-[#f21e26]"
              >
                <link.Icon
                  size={20}
                  className="text-[#fff] transition-colors duration-350 group-hover:text-white"
                />
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-[0.85rem] sm:text-[0.9rem] font-semibold text-white m-0
                             whitespace-normal break-words
                             text-center w-full tracking-[0.03em]"
                >
                  {link.title}
                </p>
              </div>

              {/* Arrow */}
              <div className="shrink-0 ml-3 flex items-center">
                <ChevronRight
                  size={18}
                  className="text-white transition-transform duration-350 group-hover:translate-x-[3px]"
                />
              </div>
            </motion.a>
          ))}
        </div>
      </div>

      {/* Popup modal */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center
                       bg-black/35 backdrop-blur-[8px] p-5"
            variants={popupOverlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={() => setShowPopup(false)}
          >
            <motion.div
              className="relative w-full max-w-[480px] rounded-md overflow-hidden
                         bg-white border border-black/[0.06]
                         shadow-[0_25px_60px_rgba(0,0,0,0.15),0_0_40px_rgba(212,5,17,0.06)]
                         md:max-w-[500px] sm:rounded-md"
              variants={popupCard}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative w-full overflow-hidden">
                <button
                  className="absolute top-3 right-3 z-10
                             w-9 h-9 rounded-full border-none
                             bg-black/50 backdrop-blur-[4px] text-white
                             flex items-center justify-center cursor-pointer
                             transition-all duration-250
                             hover:bg-[#d40511] hover:rotate-90 hover:scale-110"
                  onClick={() => setShowPopup(false)}
                  aria-label="Close popup"
                >
                  <X size={18} />
                </button>
                <img
                  src="/Home/PopuQR.jpg"
                  alt="TCS Promotionals"
                  className="w-full h-auto block"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes qrlink-ring-spin {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes qrlink-fade-in-down {
          from { opacity: 0; transform: translateY(-20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes qrlink-fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default QRLink;
